/* SHOP DTX — JavaScript du thème (sans dépendance).
 * Chaque composant est un élément personnalisé : il s'initialise tout seul,
 * y compris quand l'éditeur Shopify recharge une section, sans conflit
 * entre plusieurs occurrences d'une même section.
 */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const routes = theme.routes;
  const strings = theme.strings;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------ */
  /* Utilitaires                                                         */
  /* ------------------------------------------------------------------ */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

  /* Montants au format de la boutique (shop.money_format) */
  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}} €') => {
    const value = Math.max(0, Math.round(Number(cents) || 0));
    if (theme.currency && theme.locale) {
      try { return new Intl.NumberFormat(theme.locale, { style: 'currency', currency: theme.currency }).format(value / 100); } catch (_) {}
    }
    const group = (n, dec, thou, decimals) => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const withThou = int.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return frac ? withThou + dec + frac : withThou;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      switch (kind) {
        case 'amount_no_decimals':
          return group(value, '.', ',', 0);
        case 'amount_with_comma_separator':
          return group(value, ',', '.', 2);
        case 'amount_no_decimals_with_comma_separator':
          return group(value, ',', '.', 0);
        case 'amount_with_space_separator':
          return group(value, ',', ' ', 2);
        case 'amount_with_period_and_space_separator':
          return group(value, '.', ' ', 2);
        case 'amount_with_apostrophe_separator':
          return group(value, '.', "'", 2);
        default:
          return group(value, '.', ',', 2);
      }
    });
  };
  theme.formatMoney = formatMoney;

  if (!customElements.get('localization-select')) {
    customElements.define(
      'localization-select',
      class extends HTMLElement {
        connectedCallback() {
          const select = this.querySelector('select');
          if (!select) return;
          select.addEventListener('change', () => {
            const form = this.closest('form');
            if (form) form.submit();
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Header : hauteur (ancres), menu mobile                              */
  /* ------------------------------------------------------------------ */
  class HeaderBar extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section');
      if (this.dataset.sticky === 'true' && this.wrapper) {
        this.wrapper.classList.add('shopify-section-header-sticky');
      }
      this.updateOffset = this.updateOffset.bind(this);
      this.resizeObserver = new ResizeObserver(this.updateOffset);
      this.resizeObserver.observe(this);
      this.updateOffset();

      this.toggle = this.querySelector('[data-menu-toggle]');
      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.setMenu(this.toggle.getAttribute('aria-expanded') !== 'true'));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
            this.setMenu(false);
            this.toggle.focus();
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    updateOffset() {
      const sticky = this.dataset.sticky === 'true';
      document.documentElement.style.setProperty('--header-offset', sticky ? `${this.offsetHeight}px` : '0px');
    }

    setMenu(open) {
      this.toggle.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-menu-open', open);
      this.toggle.querySelector('[data-icon-open]').hidden = open;
      this.toggle.querySelector('[data-icon-close]').hidden = !open;
    }
  }
  if (!customElements.get('header-bar')) customElements.define('header-bar', HeaderBar);

  /* ------------------------------------------------------------------ */
  /* Barre d'annonce                                                     */
  /* ------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.track = this.querySelector('[data-track]');
      this.toggleBtn = this.querySelector('[data-toggle]');
      this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;
      this.userPaused = false;
      this.hoverPaused = false;

      this.querySelector('[data-prev]').addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]').addEventListener('click', () => this.go(this.index + 1, true));

      if (this.toggleBtn) {
        if (!this.autoplay) {
          this.toggleBtn.hidden = true;
        }
        this.toggleBtn.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.syncToggle();
          this.schedule();
        });
      }

      this.addEventListener('mouseenter', () => {
        this.hoverPaused = true;
        this.schedule();
      });
      this.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        this.schedule();
      });
      this.addEventListener('focusin', () => {
        this.hoverPaused = true;
        this.schedule();
      });
      this.addEventListener('focusout', () => {
        this.hoverPaused = false;
        this.schedule();
      });

      this.abort = new AbortController();
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) {
          this.userPaused = true;
          this.go(i, false);
          this.schedule();
        }
      }, { signal: this.abort.signal });

      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
    }

    syncToggle() {
      if (!this.toggleBtn) return;
      this.toggleBtn.setAttribute('aria-pressed', String(this.userPaused));
      this.toggleBtn.setAttribute('aria-label', this.userPaused ? strings.play : strings.pause);
      this.toggleBtn.querySelector('[data-icon-pause]').hidden = this.userPaused;
      this.toggleBtn.querySelector('[data-icon-play]').hidden = !this.userPaused;
    }

    go(i, manual) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        s.hidden = n !== this.index;
      });
      if (manual && this.track) this.track.setAttribute('aria-live', 'polite');
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.userPaused || this.hoverPaused) return;
      if (this.track) this.track.setAttribute('aria-live', 'off');
      this.timer = setTimeout(() => this.go(this.index + 1, false), Number(this.dataset.speed) || 6000);
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------ */
  /* Sélecteur de quantité                                               */
  /* ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const current = Number(this.input.value) || 0;
          const next = btn.name === 'plus' ? Math.min(current + 1, max) : Math.max(current - 1, min);
          if (next === current) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ */
  /* Panier : Cart API + Section Rendering API                           */
  /* ------------------------------------------------------------------ */
  class CartError extends Error {}

  const Cart = {
    queue: Promise.resolve(),

    sectionIds() {
      const ids = ['cart-drawer'];
      const main = document.querySelector('[data-main-cart-section]');
      if (main) ids.push(main.dataset.mainCartSection);
      return ids;
    },

    async request(url, options) {
      let response;
      try {
        response = await fetch(url, options);
      } catch (err) {
        throw new CartError(strings.networkError);
      }
      let data = null;
      try {
        data = await response.json();
      } catch (err) {
        data = null;
      }
      if (!response.ok || (data && data.status && data.status >= 400)) {
        const message = (data && (data.description || data.message)) || strings.error;
        throw new CartError(message);
      }
      if (data && Number.isFinite(data.total_price) && Array.isArray(data.items)) this.lastCart = data;
      return data;
    },

    add(formData) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const offer = formData.get('dtx_offer_product') ? {
        product: String(formData.get('dtx_offer_product')),
        variant: String(formData.get('id')),
        price: Number(formData.get('dtx_offer_price')),
        title: String(formData.get('dtx_offer_title'))
      } : null;
      ['dtx_offer_product','dtx_offer_price','dtx_offer_title'].forEach(key => formData.delete(key));
      const run = async () => {
        let before;
        if (offer) {
          before = await this.request(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
          const eligible = before.items.some(item => String(item.product_id) === offer.product);
          const alreadyUsed = before.items.some(item => (item.line_level_discount_allocations || []).some(a => a.discount_application.title === offer.title && Number(a.amount) > 0));
          if (!eligible || alreadyUsed || !Number.isFinite(offer.price)) throw new CartError(strings.offerUnavailable);
        }
        const data = await this.request(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        if (offer) {
          const after = await this.request(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
          const applied = after.items.some(item => String(item.variant_id) === offer.variant && (item.line_level_discount_allocations || []).some(a => a.discount_application.title === offer.title && Number(a.amount) > 0));
          if (!applied || after.total_price - before.total_price > offer.price) {
            const added = data.items?.[0] || data;
            const line = after.items.find(item => item.key === added.key) || after.items.find(item => String(item.variant_id) === offer.variant);
            const prior = before.items.find(item => item.key === line?.key);
            if (line) {
              const reverted = await this.request(`${routes.cartChange}.js`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: prior?.quantity || 0, sections: this.sectionIds(), sections_url: location.pathname })
              });
              await this.render(reverted.sections);
            }
            throw new CartError(strings.offerUnavailable);
          }
        }
        return data;
      };
      // Même file que les modifications : réponses appliquées dans l'ordre des actions.
      const p = this.queue.then(run, run);
      this.queue = p.catch(() => {});
      return p;
    },

/* Modification d'une ligne par sa clé (identifiant stable), jamais par sa position. */
    change(key, quantity) {
      const run = () =>
        this.request(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: this.sectionIds(),
            sections_url: window.location.pathname
          })
        });
      // File d'attente : une requête à la fois, dans l'ordre des clics.
      const p = this.queue.then(run, run);
      this.queue = p.catch(() => {});
      return p;
    },

    async render(sections) {
      let payload = sections;
      if (!payload || Object.values(payload).every((v) => !v)) {
        try {
          const res = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`);
          payload = await res.json();
        } catch (err) {
          window.location.reload();
          return;
        }
      }

      const positions = new Map([...document.querySelectorAll('[data-cart-render] .cart-item')].map(el => [el.closest('[data-cart-render]').dataset.cartRender + ':' + el.dataset.key, el.getBoundingClientRect().top]));
      const activeId = document.activeElement ? document.activeElement.id : '';
      const scrolls = new Map([...document.querySelectorAll('[data-cart-scroll]')].map(el => [el.closest('[data-cart-render]')?.dataset.cartRender, el.scrollTop]));
      let count = null;
      let total = null;

      Object.entries(payload).forEach(([, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        doc.querySelectorAll('[data-cart-render]').forEach((fresh) => {
          const key = fresh.dataset.cartRender;
          const current = document.querySelector(`[data-cart-render="${key}"]`);
          if (current) {
            current.innerHTML = fresh.innerHTML;
            Object.assign(current.dataset, fresh.dataset);
          }
          if (fresh.dataset.cartCountValue !== undefined) count = Number(fresh.dataset.cartCountValue);
          if (fresh.dataset.cartTotalValue !== undefined) total = Number(fresh.dataset.cartTotalValue);
        });
      });

      document.querySelectorAll('[data-cart-scroll]').forEach(el => { el.scrollTop = scrolls.get(el.closest('[data-cart-render]')?.dataset.cartRender) || 0; });
      if (!reducedMotion.matches) {
        document.querySelectorAll('[data-cart-render] .cart-item').forEach(el => {
          const oldTop = positions.get(el.closest('[data-cart-render]').dataset.cartRender + ':' + el.dataset.key);
          const delta = oldTop === undefined ? 10 : oldTop - el.getBoundingClientRect().top;
          if (oldTop === undefined || Math.abs(delta) > 1) el.animate([
            { transform: `translateY(${delta}px)`, opacity: oldTop === undefined ? 0 : 0.8 },
            { transform: 'translateY(0)', opacity: 1 }
          ], { duration: 360, easing: 'cubic-bezier(.22,.8,.24,1)' });
        });
        document.querySelectorAll('[data-cart-total]').forEach(el => el.animate([
          { opacity: 0.45, transform: 'translateY(4px) scale(.98)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }
        ], { duration: 220, easing: 'ease-out' }));
      }
      if (count !== null) this.updateCount(count);
      if (total !== null) {
        theme.cartTotal = total;
        document.dispatchEvent(new CustomEvent('dtx:cart-updated', { detail: { count, total } }));
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.afterRender) drawer.afterRender();

      if (activeId) {
        const again = document.getElementById(activeId);
        if (again) again.focus({ preventScroll: true });
      }
    },

    lastCount: null,

    updateCount(count) {
      const changed = this.lastCount !== null && this.lastCount !== count;
      this.lastCount = count;
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count > 99 ? '99+' : String(count);
        el.hidden = count === 0;
        if (changed && !reducedMotion.matches) {
          el.classList.remove('is-bumped');
          void el.offsetWidth; // relance l'animation
          el.classList.add('is-bumped');
        }
      });
      const label = count === 1 ? strings.cartLabelOne : strings.cartLabelMany;
      document.querySelectorAll('[data-cart-open]').forEach((el) => {
        const base = count === 0 ? strings.cartLabel : label;
        el.setAttribute('aria-label', base.replace(/\d+/, String(count)));
      });
    },

    async refresh() {
      try {
        const res = await fetch(`${window.location.pathname}?sections=${this.sectionIds().join(',')}`);
        if (!res.ok) return;
        await this.render(await res.json());
      } catch (err) {
        /* silencieux : l'affichage serveur reste valable */
      }
    }
  };
  window.SHOPDTXCart = Cart;

  /* Tiroir du panier + panneau de suggestions différé */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.opener = null;
      this.suggestEnabled = this.dataset.suggestEnabled === 'true';
      this.suggestDelay = Number(this.dataset.suggestDelay || 3000);
      this.resetSuggest();

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-suggest-close]')) {
          e.preventDefault();
          this.dismissSuggest();
          return;
        }
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
      // Échap : ferme d'abord les suggestions si le focus y est, sinon le panier (avec animation).
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        const suggest = this.dialog.querySelector('[data-cart-suggest]');
        if (suggest && suggest.contains(document.activeElement) && this.suggestShown) {
          this.dismissSuggest();
          return;
        }
        this.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('has-modal');
        this.dialog.classList.remove('is-closing');
        this.resetSuggest();
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.suggestTimer);
      clearTimeout(this.closeTimer);
    }

    open(opener) {
      if (this.dialog.open && !this.dialog.classList.contains('is-closing')) return; // déjà ouvert : on ne vole pas le focus
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      this.opener = opener || document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
      if (!reducedMotion.matches) this.dialog.querySelectorAll('.cart-item').forEach((row, i) => row.animate([{ opacity: 0, transform: 'translateX(20px)' }, { opacity: 1, transform: 'translateX(0)' }], { duration: 420, delay: Math.min(i * 55, 220) + 70, easing: 'cubic-bezier(.22,.8,.24,1)', fill: 'backwards' }));
      document.documentElement.classList.add('has-modal');
      const title = this.dialog.querySelector('#CartDrawer-Title');
      if (title) title.focus();
      this.scheduleSuggest();
    }

    close() {
      if (!this.dialog.open) return;
      clearTimeout(this.suggestTimer);
      if (reducedMotion.matches) {
        this.dialog.close();
        return;
      }
      this.dialog.classList.add('is-closing');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.dialog.close(), 280);
    }

    /* --- Suggestions --- */
    resetSuggest() {
      clearTimeout(this.suggestTimer);
      this.suggestTimer = null;
      this.suggestShown = false;
      this.suggestDismissed = false;
      if (this.dialog) this.dialog.classList.remove('is-suggest-visible', 'is-suggest-static', 'is-suggest-dismissed');
    }

    hasSuggest() {
      return Boolean(this.dialog.querySelector('[data-cart-suggest] [data-suggest-item]'));
    }

    scheduleSuggest() {
      if (!this.suggestEnabled || this.suggestShown || this.suggestDismissed || this.suggestTimer) return;
      if (!this.dialog.open || !this.hasSuggest()) return;
      this.suggestTimer = setTimeout(() => {
        this.suggestTimer = null;
        if (!this.dialog.open || this.suggestDismissed || !this.hasSuggest()) return;
        this.suggestShown = true;
        this.dialog.classList.add('is-suggest-visible'); // pas de déplacement du focus
      }, this.suggestDelay);
    }

    dismissSuggest() {
      const hadFocus = this.dialog.querySelector('[data-cart-suggest]')?.contains(document.activeElement);
      clearTimeout(this.suggestTimer);
      this.suggestTimer = null;
      this.suggestDismissed = true;
      this.dialog.classList.remove('is-suggest-visible', 'is-suggest-static');
      this.dialog.classList.add('is-suggest-dismissed');
      if (hadFocus) {
        const title = this.dialog.querySelector('#CartDrawer-Title');
        if (title) title.focus();
      }
    }

    afterRender() {
      if (!this.dialog.open) return;
      if (this.suggestDismissed) {
        // Fermé volontairement : rester fermé jusqu'à la prochaine ouverture du panier.
        this.dialog.classList.remove('is-suggest-visible', 'is-suggest-static');
        return;
      }
      if (this.suggestShown) {
        // Déjà affiché : pas de nouvelle animation à chaque modification de quantité.
        this.dialog.classList.add('is-suggest-static');
        if (!this.hasSuggest()) this.dialog.classList.remove('is-suggest-visible');
        else this.dialog.classList.add('is-suggest-visible');
      } else {
        this.scheduleSuggest();
      }
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-open]');
    if (!trigger) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* Prix d'une suggestion selon la variante choisie (données issues de Shopify) */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-suggest-variant]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const form = select.closest('form');
    if (!option || !form) return;
    const price = form.querySelector('[data-suggest-price]');
    const compare = form.querySelector('[data-suggest-compare]');
    if (price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
  });

  /* Lignes du panier (tiroir et page)
   * - Temporisation PAR CLÉ de ligne : modifier A puis B rapidement envoie les deux changements.
   * - Les minuteurs vivent hors de l'élément (il est remplacé à chaque actualisation du panier).
   * - Une suppression annule la quantité différée de la même ligne : rien ne réapparaît.
   * - Les requêtes passent par la file de Cart (ordre conservé, pas de réponse hors d'ordre). */
  const LineUpdates = {
    timers: new Map(),
    removed: new Set(),
    inflight: 0,

    schedule(key, quantity, wait = 350) {
      if (!key || this.removed.has(key)) return;
      clearTimeout(this.timers.get(key));
      this.timers.set(
        key,
        setTimeout(() => {
          this.timers.delete(key);
          this.send(key, quantity);
        }, wait)
      );
    },

    remove(key) {
      if (!key) return;
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
      this.removed.add(key);
      this.send(key, 0);
    },

    setBusy(on) {
      document.querySelectorAll('cart-items').forEach((el) => el.setAttribute('aria-busy', on ? 'true' : 'false'));
      document.querySelectorAll('[data-cart-render]').forEach((el) => {
        if (on) el.setAttribute('aria-busy', 'true');
        else el.removeAttribute('aria-busy');
      });
    },

    findRow(key) {
      return document.querySelector(`[data-cart-render] .cart-item[data-key="${CSS.escape(key)}"]`);
    },

    async send(key, quantity) {
      if (quantity > 0 && this.removed.has(key)) return; // ligne supprimée entre-temps
      if (quantity > 0 && !this.findRow(key)) return; // ligne absente du panier affiché
      this.inflight += 1;
      this.setBusy(true);
      try {
        const exits = [];
        if (quantity === 0 && !reducedMotion.matches) {
          document.querySelectorAll(`[data-cart-render] .cart-item[data-key="${CSS.escape(key)}"]`).forEach(row => {
            const animation = row.animate([
              { opacity: 1, transform: 'translateX(0) scale(1)' },
              { opacity: 0, transform: 'translateX(30px) scale(.97)' }
            ], { duration: 230, easing: 'cubic-bezier(.5,0,.7,.3)', fill: 'forwards' });
            row._dtxRemoval = animation;
            exits.push(animation.finished.catch(() => {}));
          });
        }
        const cart = await Cart.change(key, quantity);
        await Promise.all(exits);
        await Cart.render(cart.sections);
        const updated = cart.items ? cart.items.find((i) => i.key === key) : null;
        if (!updated) this.removed.delete(key);
        if (updated && quantity > updated.quantity) {
          const host = document.querySelector(`[data-cart-render] [data-line-error-key="${CSS.escape(key)}"]`);
          const message = strings.quantityLimit.replace('[quantity]', updated.quantity);
          if (host) {
            host.textContent = message;
            host.hidden = false;
          }
          announce(message);
        } else {
          announce(strings.updated);
        }
      } catch (err) {
        if (quantity === 0) this.removed.delete(key); // suppression refusée : la ligne existe toujours
        document.querySelectorAll(`[data-cart-render] .cart-item[data-key="${CSS.escape(key)}"]`).forEach(row => {
          row._dtxRemoval?.cancel();
          row._dtxRemoval = null;
          row.classList.remove('is-removing');
          const remove = row.querySelector('[data-remove-key]');
          if (remove) remove.removeAttribute('aria-disabled');
          const host = row.querySelector('[data-line-error-key]');
          if (host) {
            host.textContent = err.message;
            host.hidden = false;
          }
        });
        announce(err.message);
      } finally {
        // L'état de chargement se termine toujours, même après une erreur.
        this.inflight = Math.max(0, this.inflight - 1);
        if (this.inflight === 0) this.setBusy(false);
      }
    }
  };
  window.SHOPDTXLineUpdates = LineUpdates;

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[data-line-key]');
        if (!input) return;
        LineUpdates.schedule(input.dataset.lineKey, Math.max(0, parseInt(input.value, 10) || 0));
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-key]');
        if (!remove) return;
        e.preventDefault();
        if (remove.getAttribute('aria-disabled') === 'true') return; // suppression déjà demandée
        remove.setAttribute('aria-disabled', 'true');
        const row = remove.closest('.cart-item');
        if (row) row.classList.add('is-removing');
        LineUpdates.remove(remove.dataset.removeKey);
      });
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  class CartNote extends HTMLElement {
    connectedCallback() {
      const textarea = this.querySelector('textarea');
      if (!textarea) return;
      textarea.addEventListener(
        'input',
        debounce(() => {
          fetch(`${routes.cartUpdate}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: textarea.value })
          }).catch(() => {});
        }, 500)
      );
    }
  }
  if (!customElements.get('cart-note')) customElements.define('cart-note', CartNote);

  /* Confirmation visuelle « Ajouté » sur le bouton (retrouvé par son id après actualisation) */
  const confirmAdded = (button) => {
    const target = (button.id && document.getElementById(button.id)) || button;
    const label = target.querySelector('.button__label');
    if (!label) return;
    if (!target.dataset.labelHtml) target.dataset.labelHtml = label.innerHTML;
    target.classList.add('is-added');
    label.innerHTML = `<svg class="icon icon--tiny" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>${strings.addedShort}`;
    setTimeout(() => {
      if (!document.contains(target)) return;
      target.classList.remove('is-added');
      label.innerHTML = target.dataset.labelHtml;
      delete target.dataset.labelHtml;
    }, 1600);
  };

  /* Ajout au panier (fiche produit, ajout rapide, suggestions) */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    const drawer = document.querySelector('cart-drawer');
    if (theme.cartType !== 'drawer' || !drawer) return; // formulaire natif → page panier

    e.preventDefault();
    const button = e.submitter && e.submitter.matches('[data-add-button]') ? e.submitter : form.querySelector('[data-add-button]');
    if (!button || button.classList.contains('is-loading')) return; // anti double clic
    const errorBox = form.querySelector('[data-form-error]');
    if (errorBox) errorBox.hidden = true;
    const isSuggestion = form.hasAttribute('data-suggest-form');

    const wasDisabled = button.disabled;
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData(form);
      const data = await Cart.add(formData);
      document.dispatchEvent(new CustomEvent('dtx:cart-added', { detail: { id: String(formData.get('id')), quantity: Number(formData.get('quantity') || 1) } }));
      await Cart.render(data.sections);
      announce(strings.added);
      drawer.open(button);
      if (isSuggestion) {
        const status = document.querySelector('[data-cart-render="drawer"] [data-suggest-status]');
        if (status) status.textContent = strings.suggestAdded;
        const fresh = button.id ? document.getElementById(button.id) : null;
        if (fresh) confirmAdded(fresh);
      } else {
        confirmAdded(button);
      }
    } catch (err) {
      const visibleError = isSuggestion ? document.querySelector('[data-cart-offer-error]') || errorBox : errorBox;
      if (visibleError && document.contains(visibleError)) {
        visibleError.textContent = err.message;
        visibleError.hidden = false;
      }
      announce(err.message);
    } finally {
      // L'état de chargement se termine toujours, y compris après une erreur.
      [button, button.id ? document.getElementById(button.id) : null].forEach((b) => {
        if (!b) return;
        b.classList.remove('is-loading');
        b.removeAttribute('aria-busy');
        if (!wasDisabled) b.removeAttribute('aria-disabled');
      });
    }
  });

  /* Menu de langue : fermeture au clic extérieur et à la touche Échap */
  class LanguageMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.onDoc = (e) => {
        if (this.details.open && !this.contains(e.target)) this.details.open = false;
      };
      document.addEventListener('click', this.onDoc);
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.details.open) {
          e.stopPropagation();
          this.details.open = false;
          this.querySelector('summary').focus();
        }
      });
      this.addEventListener('focusout', (e) => {
        if (this.details.open && e.relatedTarget && !this.contains(e.relatedTarget)) this.details.open = false;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDoc);
    }
  }
  if (!customElements.get('language-menu')) customElements.define('language-menu', LanguageMenu);

  /* ------------------------------------------------------------------ */
  /* Livraison offerte : estimation sur la fiche produit                 */
  /* Panier actuel (Shopify) + offre sélectionnée × quantité.            */
  /* Le panier réel reste la référence : le message le précise.          */
  /* ------------------------------------------------------------------ */
  const ProductShipping = {
    init(root = document) {
      root.querySelectorAll('[data-shipping-progress="product"]').forEach((el) => {
        if (el.dataset.spBound) return;
        el.dataset.spBound = 'true';
        const info = el.closest('product-info');
        const state = { price: null, consumed: false };
        const abort = new AbortController();
        el._shippingAbort = abort;
        const qtyInput = info ? document.getElementById(`Quantity-${info.dataset.sectionId}`) : null;
        const readQty = () => {
          if (!qtyInput) return 1;
          const q = parseInt(qtyInput.value, 10);
          return Number.isFinite(q) && q > 0 ? q : 1;
        };
        const update = () => {
          const threshold = Number(el.dataset.threshold) || 0;
          if (!threshold) return;
          const cartTotal = Number.isFinite(theme.cartTotal) ? theme.cartTotal : Number(el.dataset.cartTotal) || 0;
          const selection = !state.consumed && state.price !== null ? state.price * readQty() : 0;
          const estimate = cartTotal + selection;
          const remaining = Math.max(0, threshold - estimate);
          const pct = Math.min(100, Math.max(0, Math.floor((estimate * 100) / threshold)));
          const reached = estimate >= threshold;
          const msg = el.querySelector('[data-sp-message]');
          const bar = el.querySelector('[data-sp-bar]');
          const tplRemaining = el.querySelector('[data-sp-remaining-template]');
          const tplReached = el.querySelector('[data-sp-reached-template]');
          if (msg) {
            if (reached) msg.textContent = tplReached ? tplReached.innerHTML.trim() : '';
            else if (tplRemaining) msg.innerHTML = tplRemaining.innerHTML.trim().replace('[amount]', formatMoney(remaining));
          }
          if (bar) {
            bar.setAttribute('aria-valuenow', String(pct));
            const fill = bar.querySelector('span');
            if (fill) fill.style.width = `${pct}%`;
          }
          el.classList.toggle('is-reached', reached);
          el.dataset.estimate = String(estimate);
          const icon = el.querySelector('.shipping-progress__icon');
          if (icon) icon.dataset.state = reached ? 'done' : 'todo';
        };
        if (info) {
          info.addEventListener('variant:change', (e) => {
            const p = e.detail && Number(e.detail.price);
            state.price = Number.isFinite(p) && e.detail.available !== false ? p : null;
            state.consumed = false;
            update();
          }, { signal: abort.signal });
          // Valeur initiale : variante déjà sélectionnée
          const checked = info.querySelector('[data-pack-input]:checked');
          if (checked) state.price = Number(checked.dataset.price);
          else {
            try {
              const variants = JSON.parse(info.querySelector('[data-variants-json]').textContent);
              const idInput = info.form ? info.form.querySelector('[data-variant-input]') : null;
              const current = variants.find((v) => idInput && String(v.id) === idInput.value) || variants[0];
              if (current && current.available) state.price = Number(current.price);
            } catch (err) {
              /* sans données : estimation = panier seul */
            }
          }
        }
        if (qtyInput) {
          const changed = () => { state.consumed = false; update(); };
          qtyInput.addEventListener('change', changed, { signal: abort.signal });
          qtyInput.addEventListener('input', changed, { signal: abort.signal });
        }
        document.addEventListener('dtx:cart-updated', update, { signal: abort.signal });
        document.addEventListener('dtx:cart-added', e => {
          const id = info?.form?.querySelector('[data-variant-input]')?.value;
          if (id && String(e.detail.id) === id) state.consumed = true;
        }, { signal: abort.signal });
        update();
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Avis : « Voir tous les avis »                                       */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-reviews-more]');
    if (!btn) return;
    const section = btn.closest('.product-reviews, .dtx-reviews');
    if (!section) return;
    const extra = [...section.querySelectorAll('[data-review-extra]')];
    extra.forEach((li) => {
      li.hidden = false;
    });
    if (extra[0]) {
      const first = extra[0].querySelector('article');
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: false });
      }
    }
    btn.closest('.product-reviews__more')?.remove();
  });

  /* ------------------------------------------------------------------ */
  /* Footer : colonnes repliables sur téléphone                          */
  /* ------------------------------------------------------------------ */
  const footerMq = window.matchMedia('(max-width: 749px)');
  const syncFooterAccordions = () => {
    document.querySelectorAll('[data-footer-acc]').forEach((d) => {
      if (footerMq.matches) {
        if (!d.dataset.userToggled) d.open = false;
      } else {
        d.open = true;
      }
    });
  };
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('[data-footer-acc] > summary');
    if (!summary) return;
    if (!footerMq.matches) {
      e.preventDefault(); // toujours ouvert sur ordinateur
      return;
    }
    summary.parentElement.dataset.userToggled = 'true';
  });
  footerMq.addEventListener('change', syncFooterAccordions);

  /* ------------------------------------------------------------------ */
  /* Pop-up des offres                                                   */
  /* Temps de visite ACTIVE cumulé (onglet visible) entre les pages de   */
  /* la session ; une présentation par session ; fermeture mémorisée ;   */
  /* jamais par-dessus une autre fenêtre ouverte.                        */
  /* ------------------------------------------------------------------ */
  const safeStore = (store) => ({
    get(k) {
      try {
        return window[store].getItem(k);
      } catch (err) {
        return null;
      }
    },
    set(k, v) {
      try {
        window[store].setItem(k, v);
      } catch (err) {
        /* stockage indisponible : le pop-up reste discret */
      }
    }
  });
  const session = safeStore('sessionStorage');
  const local = safeStore('localStorage');

  class OfferPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.version = this.dataset.version || '1';
      this.delayMs = (Number(this.dataset.delay) || 300) * 1000;
      this.frequency = this.dataset.frequency || 'session';
      this.k = {
        active: 'dtx-popup-active-ms',
        shown: `dtx-popup-shown-${this.version}`,
        dismissed: `dtx-popup-dismissed-${this.version}`,
        last: `dtx-popup-last-${this.version}`
      };

      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
        if (e.target.closest('[data-popup-close]')) {
          e.preventDefault();
          this.close();
        }
        if (e.target.closest('[data-popup-primary]')) this.remember();
      });
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('has-modal');
        if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      });

      if (this.dataset.designMode === 'true') {
        this.onSelect = (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.open(true);
        };
        this.onDeselect = (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId && this.dialog.open) this.dialog.close();
        };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
        return; // dans l'éditeur : uniquement l'aperçu à la demande
      }
      if (this.dataset.enabled !== 'true' || !this.eligible()) return;
      this.last = performance.now();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
      if (this.onDeselect) document.removeEventListener('shopify:section:deselect', this.onDeselect);
    }

    eligible() {
      if (session.get(this.k.dismissed) || session.get(this.k.shown)) return false;
      if (this.frequency !== 'session') {
        const last = Number(local.get(this.k.last)) || 0;
        const span = this.frequency === 'week' ? 7 * 864e5 : 864e5;
        if (last && Date.now() - last < span) return false;
      }
      return true;
    }

    contextFree() {
      if (document.visibilityState !== 'visible') return false;
      if (document.querySelector('dialog[open]')) return false; // panier, galerie, autre fenêtre
      if (document.documentElement.classList.contains('has-modal')) return false;
      if (document.querySelector('.lang-menu__details[open], header-bar.is-menu-open, .facet[open]')) return false;
      const a = document.activeElement;
      if (a && a.matches && a.matches('input, textarea, select, [contenteditable="true"]')) return false; // saisie en cours
      return true;
    }

    tick() {
      const now = performance.now();
      const dt = Math.min(now - this.last, 1500); // pas de saut après une mise en veille
      this.last = now;
      if (document.visibilityState !== 'visible') return; // onglet en arrière-plan : pas de comptage
      const active = (Number(session.get(this.k.active)) || 0) + dt;
      session.set(this.k.active, String(Math.round(active)));
      if (active < this.delayMs) return;
      if (!this.eligible()) {
        clearInterval(this.timer);
        return;
      }
      if (this.contextFree()) this.open(false); // sinon : on attend que le contexte se libère
    }

    open(preview) {
      if (this.dialog.open) return;
      clearInterval(this.timer);
      this.opener = document.activeElement;
      this.dialog.showModal();
      document.documentElement.classList.add('has-modal');
      if (!preview) {
        session.set(this.k.shown, '1');
        local.set(this.k.last, String(Date.now()));
      }
      const title = this.dialog.querySelector('.offer-popup__title');
      if (title) {
        title.setAttribute('tabindex', '-1');
        title.focus({ preventScroll: true });
      }
    }

    remember() {
      session.set(this.k.dismissed, '1');
      local.set(this.k.last, String(Date.now()));
    }

    close() {
      this.remember();
      if (this.dialog.open) this.dialog.close();
    }
  }
  if (!customElements.get('offer-popup')) customElements.define('offer-popup', OfferPopup);

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) Cart.refresh();
  });

  /* ------------------------------------------------------------------ */
  /* Galerie produit                                                     */
  /* ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slides]');
      if (!this.slider) return;
      this.slides = [...this.slider.children];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.current = 0;

      if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.current - 1));
      if (this.next) this.next.addEventListener('click', () => this.goTo(this.current + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.thumb))));

      this.slider.addEventListener(
        'scroll',
        debounce(() => {
          const i = Math.round(this.slider.scrollLeft / this.slider.clientWidth);
          this.setCurrent(i);
        }, 60),
        { passive: true }
      );

      this.querySelectorAll('[data-zoom]').forEach((btn) => {
        btn.addEventListener('click', () => this.openLightbox(Number(btn.dataset.zoom), btn));
      });

      const initial = this.dataset.activeMedia;
      if (initial) this.goToMedia(initial, true);
      this.setCurrent(this.current);
      this.setupLightbox();
    }

    goToMedia(mediaId, instant) {
      if (!mediaId || !this.slides) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i, instant);
    }

    goTo(i, instant) {
      const n = Math.max(0, Math.min(i, this.slides.length - 1));
      this.slider.scrollTo({ left: n * this.slider.clientWidth, behavior: instant ? 'auto' : scrollBehavior() });
      this.setCurrent(n);
    }

    setCurrent(i) {
      this.current = Math.max(0, Math.min(i, this.slides.length - 1));
      this.thumbs.forEach((t, n) => {
        if (n === this.current) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(this.current + 1);
      if (this.prev) this.prev.disabled = this.current === 0;
      if (this.next) this.next.disabled = this.current === this.slides.length - 1;
    }

    setupLightbox() {
      if (!this.lightbox) return;
      this.lbSlides = this.lightbox.querySelector('[data-lightbox-slides]');
      this.lbCounter = this.lightbox.querySelector('[data-lightbox-current]');
      const close = this.lightbox.querySelector('[data-lightbox-close]');
      const prev = this.lightbox.querySelector('[data-lightbox-prev]');
      const next = this.lightbox.querySelector('[data-lightbox-next]');
      close.addEventListener('click', () => this.lightbox.close());
      if (prev) prev.addEventListener('click', () => this.lbGo(this.lbIndex - 1));
      if (next) next.addEventListener('click', () => this.lbGo(this.lbIndex + 1));
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
        if (e.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
      });
      this.lbSlides.addEventListener(
        'scroll',
        debounce(() => {
          this.lbIndex = Math.round(this.lbSlides.scrollLeft / this.lbSlides.clientWidth);
          if (this.lbCounter) this.lbCounter.textContent = String(this.lbIndex + 1);
        }, 60),
        { passive: true }
      );
      this.lightbox.addEventListener('close', () => {
        document.documentElement.classList.remove('has-modal');
        this.goTo(this.lbIndex, true);
        if (this.lbOpener && document.contains(this.lbOpener)) this.lbOpener.focus();
      });
    }

    openLightbox(index, opener) {
      if (!this.lightbox) return;
      this.lbOpener = opener;
      this.lightbox.showModal();
      document.documentElement.classList.add('has-modal');
      this.lbIndex = index;
      requestAnimationFrame(() => {
        this.lbSlides.scrollTo({ left: index * this.lbSlides.clientWidth, behavior: 'auto' });
        if (this.lbCounter) this.lbCounter.textContent = String(index + 1);
      });
      this.lightbox.querySelector('[data-lightbox-close]').focus();
    }

    lbGo(i) {
      const count = this.lbSlides.children.length;
      this.lbIndex = Math.max(0, Math.min(i, count - 1));
      this.lbSlides.scrollTo({ left: this.lbIndex * this.lbSlides.clientWidth, behavior: scrollBehavior() });
      if (this.lbCounter) this.lbCounter.textContent = String(this.lbIndex + 1);
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------ */
  /* Fiche produit : offre / variante, prix, bouton, URL, barre mobile   */
  /* ------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.idInput = this.form.querySelector('[data-variant-input]');
      this.addButton = this.form.querySelector('[data-add-button]');
      this.addLabel = this.form.querySelector('[data-add-label]');
      this.gallery = this.querySelector('product-gallery');
      this.packInputs = [...this.querySelectorAll('[data-pack-input]')];
      this.optionInputs = [...this.querySelectorAll('[data-option-index]')];
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants-json]').textContent);
      } catch (err) {
        this.variants = [];
      }

      if (this.packInputs.length) {
        // Avec JS : un seul champ « id » dans le formulaire (compatible paiement accéléré).
        this.packInputs.forEach((input) => {
          input.name = `pack-${this.sectionId}`;
          input.addEventListener('change', () => this.onPack(input));
        });
        const checked = this.packInputs.find((i) => i.checked) || this.packInputs.find((i) => !i.disabled);
        if (checked) {
          checked.checked = true;
          this.idInput.disabled = false;
          this.onPack(checked, true);
        }
      } else if (this.optionInputs.length) {
        this.idInput.disabled = false;
        this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptions()));
        this.onOptions(true);
      }

      this.setupSticky();
    }

    disconnectedCallback() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }

    onPack(input, initial) {
      this.setVariant(input.value, {
        available: input.dataset.available === 'true',
        mediaId: input.dataset.mediaId,
        label: input.dataset.label,
        price: Number(input.dataset.price),
        initial
      });
    }

    onOptions(initial) {
      const selected = [];
      this.optionInputs
        .filter((i) => i.checked)
        .forEach((i) => {
          selected[Number(i.dataset.optionIndex)] = i.value;
        });
      const variant = this.variants.find((v) => v.options.every((o, n) => o === selected[n]));
      if (!variant) {
        this.setVariant(null, { available: false, initial });
        return;
      }
      this.setVariant(String(variant.id), {
        available: variant.available,
        mediaId: variant.media,
        label: variant.title,
        price: Number(variant.price),
        initial
      });
    }

    setVariant(id, meta) {
      this.currentLabel = meta.label || '';
      if (id) this.idInput.value = id;

      this.querySelectorAll('[data-variant-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.variantPanel !== String(id);
      });

      if (this.addButton) {
        this.addButton.disabled = !id || !meta.available;
        if (this.addLabel) {
          this.addLabel.textContent = !id ? strings.unavailable : meta.available ? strings.addToCart : strings.soldOut;
        }
      }

      if (!meta.initial) {
        if (meta.mediaId && this.gallery && this.gallery.goToMedia) this.gallery.goToMedia(meta.mediaId);
        if (id && this.dataset.updateUrl === 'true' && this.dataset.mode !== 'products') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url.toString());
        }
        const errorBox = this.form.querySelector('[data-form-error]');
        if (errorBox) errorBox.hidden = true;
      }

      this.syncSticky();
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { id, ...meta } }));
    }

    setupSticky() {
      this.sticky = this.querySelector('[data-sticky-atc]');
      if (!this.sticky || !this.addButton) return;
      this.stickyLabel = this.sticky.querySelector('[data-sticky-label]');
      this.stickyPrice = this.sticky.querySelector('[data-sticky-price]');
      const stickyAdd = this.sticky.querySelector('[data-sticky-add]');
      const mobile = window.matchMedia('(max-width: 989px)');

      stickyAdd.addEventListener('click', () => {
        if (this.addButton.disabled) {
          const picker = this.querySelector('[data-pack-picker], [data-variant-picker]') || this.addButton;
          picker.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
          return;
        }
        if (this.form.requestSubmit) this.form.requestSubmit(this.addButton);
        else this.addButton.click();
      });

      this.stickyObserver = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        const show = passed && mobile.matches;
        this.sticky.hidden = !show;
        document.body.classList.toggle('has-sticky-atc', show);
        if (show) {
          document.body.style.setProperty('--sticky-atc-height', `${this.sticky.offsetHeight}px`);
        }
      });
      this.stickyObserver.observe(this.addButton);
      this.syncSticky();
    }

    syncSticky() {
      if (!this.sticky) return;
      const panel = this.querySelector('[data-price-panels] [data-variant-panel]:not([hidden])');
      if (this.stickyPrice) this.stickyPrice.innerHTML = panel ? panel.innerHTML : '';
      if (this.stickyLabel) this.stickyLabel.textContent = this.currentLabel && this.currentLabel !== 'Default Title' ? this.currentLabel : '';
      const stickyAdd = this.sticky.querySelector('[data-sticky-add]');
      if (stickyAdd && this.addLabel) stickyAdd.textContent = this.addLabel.textContent.trim();
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ------------------------------------------------------------------ */
  /* Avis défilants                                                      */
  /* ------------------------------------------------------------------ */
  class TestimonialMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.sectionEl = this.closest('.testimonials');
      this.controls = this.sectionEl ? this.sectionEl.querySelector('[data-controls]') : null;
      this.toggleBtn = this.controls ? this.controls.querySelector('[data-toggle]') : null;
      this.speed = Number(this.dataset.speed) || 30;
      this.pos = 0;
      this.hover = false;
      this.focus = false;
      this.touchHold = false;
      this.userPaused = false;
      this.visible = true;

      if (this.controls) {
        this.controls.querySelector('[data-prev]').addEventListener('click', () => this.step(-1));
        this.controls.querySelector('[data-next]').addEventListener('click', () => this.step(1));
        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => {
            this.userPaused = !this.userPaused;
            this.syncToggle();
          });
        }
      }

      this.addEventListener('mouseenter', () => {
        this.hover = true;
      });
      this.addEventListener('mouseleave', () => {
        this.hover = false;
        this.pos = this.scrollLeft;
      });
      this.addEventListener('focusin', () => {
        this.focus = true;
      });
      this.addEventListener('focusout', () => {
        this.focus = false;
      });
      this.addEventListener(
        'pointerdown',
        () => {
          this.holdUntil(4000);
        },
        { passive: true }
      );
      this.addEventListener(
        'wheel',
        () => {
          this.holdUntil(2500);
        },
        { passive: true }
      );

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
      });
      this.io.observe(this);

      this.setup = this.setup.bind(this);
      this.ro = new ResizeObserver(debounce(this.setup, 200));
      this.ro.observe(this);
      reducedMotion.addEventListener('change', this.setup);
      // Les polices peuvent élargir les cartes après le premier calcul : on recalcule une fois chargées.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.isConnected && this.setup());
      window.addEventListener('load', () => this.isConnected && this.setup(), { once: true });
      document.addEventListener('shopify:block:select', (e) => {
        if (this.contains(e.target)) {
          this.userPaused = true;
          this.syncToggle();
          e.target.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
      });
      this.setup();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      reducedMotion.removeEventListener('change', this.setup);
    }

    holdUntil(ms) {
      this.touchHold = true;
      clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(() => {
        this.touchHold = false;
        this.pos = this.scrollLeft;
      }, ms);
    }

    originals() {
      return [...this.track.children].filter((c) => !c.hasAttribute('data-clone'));
    }

    setup() {
      cancelAnimationFrame(this.raf);
      this.track.querySelectorAll('[data-clone]').forEach((c) => c.remove());
      this.classList.remove('is-autoplay');

      const cs = getComputedStyle(this);
      const inner = this.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const overflow = this.track.scrollWidth > inner + 4;
      if (this.controls) this.controls.hidden = !overflow;
      // Peu d'avis : cartes centrées, sans flèches ni copies (aucun avis affiché deux fois à l'écran).
      this.classList.toggle('is-static', !overflow);

      const desktop = window.matchMedia('(min-width: 750px)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && overflow && desktop && !reducedMotion.matches;
      if (this.toggleBtn) this.toggleBtn.hidden = !this.autoplay;
      if (!this.autoplay) return;

      // Copies visuelles pour une boucle continue : masquées aux lecteurs d'écran, inertes.
      this.originals().forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.removeAttribute('role');
        clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
        this.track.appendChild(clone);
      });
      this.classList.add('is-autoplay');
      const first = this.originals()[0];
      const firstClone = this.track.querySelector('[data-clone]');
      this.loopWidth = firstClone.offsetLeft - first.offsetLeft;
      this.pos = this.scrollLeft;
      this.last = performance.now();
      const tick = (now) => {
        const dt = Math.min(now - this.last, 64);
        this.last = now;
        const paused = this.userPaused || this.hover || this.focus || this.touchHold || !this.visible;
        if (!paused) {
          this.pos += (this.speed * dt) / 1000;
          if (this.pos >= this.loopWidth) this.pos -= this.loopWidth;
          this.scrollLeft = this.pos;
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    step(dir) {
      const card = this.originals()[0];
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const delta = (card.offsetWidth + gap) * dir;
      if (this.autoplay) {
        this.holdUntil(3500);
        let target = this.scrollLeft + delta;
        if (target < 0) target += this.loopWidth;
        if (target >= this.loopWidth) target -= this.loopWidth;
        this.scrollTo({ left: target, behavior: scrollBehavior() });
        this.pos = target;
      } else {
        this.scrollBy({ left: delta, behavior: scrollBehavior() });
      }
    }

    syncToggle() {
      if (!this.toggleBtn) return;
      this.toggleBtn.setAttribute('aria-pressed', String(this.userPaused));
      this.toggleBtn.querySelector('[data-icon-pause]').hidden = this.userPaused;
      this.toggleBtn.querySelector('[data-icon-play]').hidden = !this.userPaused;
      const label = this.toggleBtn.querySelector('[data-toggle-label]');
      if (label) label.textContent = this.userPaused ? strings.play : strings.pause;
      if (!this.userPaused) this.pos = this.scrollLeft;
    }
  }
  if (!customElements.get('testimonial-marquee')) customElements.define('testimonial-marquee', TestimonialMarquee);

  /* ------------------------------------------------------------------ */
  /* Recommandations produit                                             */
  /* ------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      const io = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          this.loaded = true;
          fetch(this.dataset.url)
            .then((r) => r.text())
            .then((text) => {
              const fresh = parseHTML(text).querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim().length) this.innerHTML = fresh.innerHTML;
            })
            .catch(() => {});
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------ */
  /* Tri du catalogue                                                    */
  /* ------------------------------------------------------------------ */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-autosubmit]').forEach((el) => {
        el.addEventListener('change', () => {
          const form = el.form;
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        });
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = this.querySelector('details[open]');
        if (open) {
          open.removeAttribute('open');
          open.querySelector('summary').focus();
        }
      });
    }
  }
  if (!customElements.get('facet-form')) customElements.define('facet-form', FacetForm);

  /* ------------------------------------------------------------------ */
  /* Apparition discrète au défilement                                   */
  /* ------------------------------------------------------------------ */
  const initReveal = (root = document) => {
    if (!document.body.classList.contains('has-reveal') || reducedMotion.matches || isDesignMode()) return;
    if (!('IntersectionObserver' in window)) return;
    const items = [...root.querySelectorAll('[data-reveal]:not(.reveal-done)')];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('reveal-pending');
          entry.target.classList.add('reveal-done');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    const vh = window.innerHeight;
    items.forEach((el) => {
      if (el.getBoundingClientRect().top < vh * 0.92) return; // déjà visible : pas d'effet
      el.classList.add('reveal-pending');
      io.observe(el);
    });
  };

  /* Messages de formulaire : focus après envoi */
  const focusFormMessages = () => {
    const msg = document.querySelector('[data-autofocus]');
    if (msg) {
      msg.focus({ preventScroll: false });
      msg.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  };

  const ready = () => {
    initReveal();
    focusFormMessages();
    ProductShipping.init();
    syncFooterAccordions();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll('[data-shipping-progress]').forEach(el => el._shippingAbort?.abort());
  });
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    ProductShipping.init(e.target);
    syncFooterAccordions();
  });
})();
